import { useCallback, useEffect, useState } from 'react';
import { useMiniAppApi, useRuntimeConfig } from '../lib/context';
import {
  authenticateWithTelegram,
  type TelegramAuthState,
  loadPersistedSession,
  clearPersistedSession,
} from '../lib/telegram-auth';
import { useSessionStore } from '../lib/session-store';

const INITIAL_STATE: TelegramAuthState = { status: 'loading' };

export type UseTelegramAuthResult = TelegramAuthState & {
  retry: () => Promise<void>;
};

export function useTelegramAuth(): UseTelegramAuthResult {
  const api = useMiniAppApi();
  const config = useRuntimeConfig();
  const clearSession = useSessionStore(state => state.clear);
  const [state, setState] = useState<TelegramAuthState>(INITIAL_STATE);

  const runAuth = useCallback(async () => {
    setState({ status: 'loading' });
    const result = await authenticateWithTelegram(api);
    setState(result);
  }, [api]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        await runAuth();
      } catch (error) {
        console.error('[Auth] Unexpected error during auth flow:', error);
        if (isMounted) {
          const cached = loadPersistedSession();
          if (cached) {
            setState({
              status: 'cached-session',
              session: {
                userId: cached.userId,
                sessionToken: cached.sessionToken,
                traceId: cached.traceId,
                source: 'cached',
              },
              message: 'Используем сохранённую сессию (аварийный режим).',
              error: error instanceof Error ? error.message : 'Unexpected error',
            });
          } else {
            clearPersistedSession();
            clearSession();
            setState({
              status: 'error',
              error: error instanceof Error ? error.message : 'Unexpected error',
              message: 'Не удалось инициализировать авторизацию.',
            });
          }
        }
      }
    })();
    return () => {
      isMounted = false;
      clearSession();
    };
  }, [runAuth, clearSession, config.buildVersion]);

  return {
    ...state,
    retry: runAuth,
  };
}
